import { createColumnHelper } from '@tanstack/react-table';

import { ScrollableCell } from '@/components/data-table/cells/scrollable-cell';
import i18n from '@/core/i18n';
import { formatCompactDateRange } from '@/utils/date-formatters';

import { PART_OF_DAY_LABEL_KEY } from '../../constants/time-off-part-of-day-labels';
import { TimeOffRequest } from '../../types/time-off-request';
import { TimeOffTimeCategory } from '../../types/time-off-time-category';
import { TimeOffEmployeeCell } from '../approvals-table/cells/time-off-employee-cell';
import { TimeOffActionsCell } from './cells/time-off-actions-cell';
import { TimeOffApproversWithStatusCell } from './cells/time-off-approvers-with-status-cell';
import { TimeOffDescriptionCell } from './cells/time-off-description-cell';
import { TimeOffStatusCell } from './cells/time-off-status-cell';
import { TimeOffTypeCell } from './cells/time-off-type-cell';

const columnHelper = createColumnHelper<TimeOffRequest>();

const isPastRow = (row: TimeOffRequest) => row.timeCategory === TimeOffTimeCategory.Past;

// ─── Shared column builders ─────────────────────────────────────────────────
// Exported so both the Time-Offs table and the Approvals table compose the same
// columns. Factories (not consts) so headers resolve through i18n at render time
// — a module-level const would call `i18n.t` at import time and bake in raw keys.

export const buildTimeOffTypeColumn = () =>
  columnHelper.accessor(row => row.type.displayName, {
    id: 'type',
    header: i18n.t('authenticated.timeOff:table.columns.type'),
    enableHiding: false,
    size: 180,
    minSize: 140,
    cell: ({ row }) => {
      const request = row.original;
      return (
        <TimeOffTypeCell
          code={request.type.code}
          displayName={request.type.displayName}
          isPast={isPastRow(request)}
        />
      );
    },
  });

export const buildTimeOffStatusColumn = () =>
  columnHelper.accessor('status', {
    id: 'status',
    header: i18n.t('authenticated.timeOff:table.columns.status'),
    size: 140,
    minSize: 110,
    cell: ({ row }) => (
      <TimeOffStatusCell request={row.original} isPast={isPastRow(row.original)} />
    ),
  });

export const buildTimeOffDateRangeColumn = () =>
  columnHelper.accessor('startDate', {
    id: 'dateRange',
    header: i18n.t('authenticated.timeOff:table.columns.dateRange'),
    size: 200,
    minSize: 160,
    cell: ({ row }) => {
      const isPast = isPastRow(row.original);
      const text = formatCompactDateRange(row.original.startDate, row.original.endDate);
      return <ScrollableCell text={text} className={isPast ? 'opacity-50' : undefined} />;
    },
  });

export const buildTimeOffDurationColumn = () =>
  columnHelper.accessor('totalDays', {
    id: 'duration',
    header: i18n.t('authenticated.timeOff:table.columns.duration'),
    size: 110,
    minSize: 90,
    cell: ({ row }) => {
      const isPast = isPastRow(row.original);
      const text = i18n.t('authenticated.timeOff:duration.days', {
        count: row.original.totalDays,
      });
      return <ScrollableCell text={text} className={isPast ? 'opacity-50' : undefined} />;
    },
  });

export const buildTimeOffPartOfDayColumn = () =>
  columnHelper.accessor('partOfDay', {
    id: 'partOfDay',
    header: i18n.t('authenticated.timeOff:table.columns.partOfDay'),
    size: 130,
    minSize: 110,
    cell: ({ row }) => {
      const isPast = isPastRow(row.original);
      const text = i18n.t(`authenticated.timeOff:${PART_OF_DAY_LABEL_KEY[row.original.partOfDay]}`);
      return <ScrollableCell text={text} className={isPast ? 'opacity-50' : undefined} />;
    },
  });

export const buildTimeOffApproversColumn = () =>
  columnHelper.display({
    id: 'approvers',
    header: i18n.t('authenticated.timeOff:table.columns.approvers'),
    size: 240,
    minSize: 160,
    cell: ({ row }) => (
      <TimeOffApproversWithStatusCell
        approvers={row.original.approvers}
        isPast={isPastRow(row.original)}
      />
    ),
  });

export const buildTimeOffTimeCategoryColumn = () =>
  columnHelper.accessor('timeCategory', {
    id: 'timeCategory',
    header: i18n.t('authenticated.timeOff:table.columns.timeCategory'),
    size: 120,
    minSize: 100,
    cell: ({ row }) => {
      const isPast = isPastRow(row.original);
      const text = i18n.t(
        `authenticated.timeOff:timeCategory.${row.original.timeCategory.toLowerCase()}`
      );
      return <ScrollableCell text={text} className={isPast ? 'opacity-50' : undefined} />;
    },
  });

export const buildTimeOffDescriptionColumn = () =>
  columnHelper.accessor('description', {
    id: 'description',
    header: i18n.t('authenticated.timeOff:table.columns.description'),
    size: 220,
    minSize: 160,
    cell: ({ row }) => (
      <TimeOffDescriptionCell
        description={row.original.description}
        isPast={isPastRow(row.original)}
      />
    ),
  });

export const buildTimeOffRequesterColumn = () =>
  columnHelper.accessor(row => row.requestedBy.name, {
    id: 'requestedBy',
    header: i18n.t('authenticated.timeOff:table.columns.requestedBy'),
    size: 220,
    minSize: 180,
    cell: ({ row }) => (
      <TimeOffEmployeeCell employee={row.original.requestedBy} isPast={isPastRow(row.original)} />
    ),
  });

/**
 * Time-Offs page columns (the personal "Requests & History" table). Ends with the
 * cancel action cell. The shared builders above are reused by the Approvals table.
 *
 * Headers are plain strings (not render fns) so `DataTableColumnsSelector` can use
 * them as the toggle labels.
 */
export const getTimeOffsTableColumns = ({
  shouldShowRequesterColumn = false,
  onEditRequest,
}: {
  shouldShowRequesterColumn?: boolean;
  onEditRequest: (request: TimeOffRequest) => void;
}) => {
  const columns = [
    buildTimeOffTypeColumn(),
    buildTimeOffStatusColumn(),
    buildTimeOffDateRangeColumn(),
    buildTimeOffDurationColumn(),
    buildTimeOffPartOfDayColumn(),
    buildTimeOffDescriptionColumn(),
    buildTimeOffApproversColumn(),
    buildTimeOffTimeCategoryColumn(),
  ];

  if (shouldShowRequesterColumn) {
    columns.unshift(buildTimeOffRequesterColumn());
  }

  return [
    ...columns,
    columnHelper.display({
      id: 'actions',
      header: i18n.t('authenticated.timeOff:table.columns.actions'),
      size: 70,
      minSize: 60,
      enableResizing: false,
      enableHiding: false,
      cell: ({ row }) => (
        <TimeOffActionsCell request={row.original} onEditRequest={onEditRequest} />
      ),
    }),
  ];
};
